import React, {forwardRef, useState} from 'react';
import {Pressable, StyleSheet, Text, TextInput, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import EyeIcon from '../assets/icons/eye.svg';
import EyeOffIcon from '../assets/icons/eye-off.svg';
import {colors, fonts, radii} from '../config/theme';

// Labelled text field from the Stitch auth designs: label above, 12pt
// rounded box, forest border while focused.
// required: appends a green asterisk to the label — only worth it when
//   the form also has optional fields.
// secure: hides the text and adds an eye button to reveal it.
// error: message shown under the field; also turns the border red.
// icon: optional element shown at the start of the field.
// variant: 'outline' (white box, grey border) | 'filled' (grey fill, no
//   border — reads better on a white card).
// Remaining props go straight to the TextInput.
const AuthInput = forwardRef(
  (
    {
      label,
      required = false,
      secure = false,
      icon,
      error,
      variant = 'outline',
      style,
      onFocus,
      onBlur,
      ...inputProps
    },
    ref,
  ) => {
    const {t} = useTranslation();
    const [focused, setFocused] = useState(false);
    const [hidden, setHidden] = useState(secure);
    return (
      <View style={style}>
        {label ? (
          <Text style={styles.label}>
            {label}
            {required ? <Text style={styles.required}> *</Text> : null}
          </Text>
        ) : null}
        <View
          style={[
            styles.field,
            variant === 'filled' && styles.fieldFilled,
            focused && styles.fieldFocused,
            error && styles.fieldError,
          ]}>
          {icon ? <View style={styles.leading}>{icon}</View> : null}
          <TextInput
            ref={ref}
            style={styles.input}
            placeholderTextColor={colors.textMuted}
            selectionColor={colors.secondary}
            secureTextEntry={hidden}
            accessibilityLabel={label}
            onFocus={e => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={e => {
              setFocused(false);
              onBlur?.(e);
            }}
            {...inputProps}
          />
          {secure ? (
            <Pressable
              onPress={() => setHidden(h => !h)}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={
                hidden ? t('root.Auth.showPassword') : t('root.Auth.hidePassword')
              }
              style={styles.eye}>
              {hidden ? (
                <EyeIcon width={20} height={20} color={colors.textSubtle} />
              ) : (
                <EyeOffIcon width={20} height={20} color={colors.textSubtle} />
              )}
            </Pressable>
          ) : null}
        </View>
        {error ? (
          <Text style={styles.error} accessibilityLiveRegion="polite">
            {error}
          </Text>
        ) : null}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  label: {
    fontFamily: fonts.semiBold,
    fontSize: 12,
    lineHeight: 16,
    color: colors.textStrong,
    marginBottom: 6,
  },
  required: {
    color: colors.brandBright,
  },
  field: {
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    paddingHorizontal: 16,
  },
  // The 2pt border would nudge the text, so trim the padding to match.
  fieldFocused: {
    borderWidth: 2,
    borderColor: colors.primary,
    backgroundColor: colors.surface,
    paddingHorizontal: 15,
  },
  fieldFilled: {
    height: 52,
    borderRadius: 16,
    borderColor: 'transparent',
    backgroundColor: colors.surfaceMuted,
  },
  fieldError: {
    borderColor: colors.error,
    backgroundColor: colors.errorTint,
  },
  error: {
    fontFamily: fonts.medium,
    fontSize: 12,
    lineHeight: 16,
    color: colors.error,
    marginTop: 6,
  },
  input: {
    flex: 1,
    height: '100%',
    paddingVertical: 0,
    fontFamily: fonts.medium,
    fontSize: 14,
    color: colors.textStrong,
  },
  leading: {
    marginRight: 10,
  },
  eye: {
    marginLeft: 8,
    padding: 2,
  },
});

export default AuthInput;
